const STORAGE_PREFIX = "autoWithdrawConfigV1";
const DEFAULT_COOLDOWN_SEC = 240;
const DEFAULT_CONFIG = {
  enabled: false,
  threshold: 0,
  latestDepositAmount: 0,
  latestDepositSignature: "",
  latestDepositMessageText: "",
  latestDepositCreatedAt: "",
  minThreshold: 0,
  cooldownSec: DEFAULT_COOLDOWN_SEC,
  lastAttemptAt: 0,
  lastAttemptBalance: null,
  lastStatus: "idle",
  lastMessage: "",
};

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const getAutoWithdrawStorageKey = (userId) =>
  `${STORAGE_PREFIX}:${String(userId ?? "")}`;

export const loadAutoWithdrawConfig = (userId) => {
  if (!userId || typeof window === "undefined") {
    return { ...DEFAULT_CONFIG };
  }

  const key = getAutoWithdrawStorageKey(userId);

  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return { ...DEFAULT_CONFIG };
    }

    const parsed = JSON.parse(raw);

    return {
      ...DEFAULT_CONFIG,
      enabled: Boolean(parsed?.enabled),
      threshold: Math.max(0, toNumber(parsed?.threshold, 0)),
      latestDepositAmount: Math.max(
        0,
        toNumber(parsed?.latestDepositAmount, 0),
      ),
      latestDepositSignature: String(parsed?.latestDepositSignature || ""),
      latestDepositMessageText: String(parsed?.latestDepositMessageText || ""),
      latestDepositCreatedAt: String(parsed?.latestDepositCreatedAt || ""),
      minThreshold: Math.max(0, toNumber(parsed?.minThreshold, 0)),
      cooldownSec: Math.max(
        30,
        toNumber(parsed?.cooldownSec, DEFAULT_COOLDOWN_SEC),
      ),
      lastAttemptAt: Math.max(0, toNumber(parsed?.lastAttemptAt, 0)),
      lastAttemptBalance:
        parsed?.lastAttemptBalance == null
          ? null
          : toNumber(parsed.lastAttemptBalance, null),
      lastStatus: String(parsed?.lastStatus || "idle"),
      lastMessage: String(parsed?.lastMessage || ""),
    };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
};

export const saveAutoWithdrawConfig = (userId, nextConfig) => {
  if (!userId || typeof window === "undefined") return;

  const key = getAutoWithdrawStorageKey(userId);
  const normalized = {
    enabled: Boolean(nextConfig?.enabled),
    threshold: Math.max(0, toNumber(nextConfig?.threshold, 0)),
    latestDepositAmount: Math.max(
      0,
      toNumber(nextConfig?.latestDepositAmount, 0),
    ),
    latestDepositSignature: String(nextConfig?.latestDepositSignature || ""),
    latestDepositMessageText: String(
      nextConfig?.latestDepositMessageText || "",
    ),
    latestDepositCreatedAt: String(nextConfig?.latestDepositCreatedAt || ""),
    minThreshold: Math.max(0, toNumber(nextConfig?.minThreshold, 0)),
    cooldownSec: Math.max(
      30,
      toNumber(nextConfig?.cooldownSec, DEFAULT_COOLDOWN_SEC),
    ),
    lastAttemptAt: Math.max(0, toNumber(nextConfig?.lastAttemptAt, 0)),
    lastAttemptBalance:
      nextConfig?.lastAttemptBalance == null
        ? null
        : toNumber(nextConfig?.lastAttemptBalance, null),
    lastStatus: String(nextConfig?.lastStatus || "idle"),
    lastMessage: String(nextConfig?.lastMessage || ""),
  };

  localStorage.setItem(key, JSON.stringify(normalized));
};

export const getNumericBalance = (rawBalance) => {
  if (typeof rawBalance === "number") return rawBalance;
  if (typeof rawBalance === "string") {
    const sanitized = rawBalance.replace(/,/g, "").trim();
    const n = Number(sanitized);
    return Number.isFinite(n) ? n : 0;
  }
  if (Array.isArray(rawBalance)) {
    return getNumericBalance(rawBalance[0]);
  }
  if (rawBalance && typeof rawBalance === "object") {
    const candidates = [
      rawBalance.balance,
      rawBalance.amount,
      rawBalance.credit,
      rawBalance.wallet,
      rawBalance.playerBalance,
      rawBalance.data,
    ];
    for (const item of candidates) {
      const n = getNumericBalance(item);
      if (Number.isFinite(n) && n !== 0) return n;
    }
  }
  return 0;
};
